/**
 * Parses the Contentful riseActivity `expiration` value into a Date.
 *
 * Contentful stores the expiration as a compact ISO 8601 basic-format string,
 * e.g. `20270624T000000-0700`, which `new Date()` cannot parse directly. This
 * normalizes it to extended format (`2027-06-24T00:00:00-07:00`) first.
 *
 * @param {string} expiration - Compact ISO 8601 expiration string
 * @returns {Date|null} Parsed Date, or null when the value is missing/invalid
 */
export const parseRiseExpiration = expiration => {
  if (!expiration || typeof expiration !== 'string') {
    return null
  }

  const match = expiration.match(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})([+-]\d{2})(\d{2})$/,
  )

  if (!match) {
    const fallbackDate = new Date(expiration)
    return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate
  }

  const [, year, month, day, hour, minute, second, tzHour, tzMinute] = match
  const isoString = `${year}-${month}-${day}T${hour}:${minute}:${second}${tzHour}:${tzMinute}`
  const parsedDate = new Date(isoString)

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

/**
 * Converts a Contentful riseActivity `expiration` value into an absolute ISO
 * 8601 UTC timestamp (e.g. `2027-06-24T07:00:00.000Z`) suitable for the
 * experience-player service's `expireAt` parameter. Returns `undefined` when the
 * expiration is missing or invalid, so the caller can omit it.
 *
 * @param {string} expiration - Compact ISO 8601 expiration string
 * @returns {string|undefined} ISO 8601 timestamp, or undefined
 */
export const getExpireAtFromExpiration = expiration => {
  const expirationDate = parseRiseExpiration(expiration)
  return expirationDate ? expirationDate.toISOString() : undefined
}

/**
 * Extracts the experience-player launch info from a learningExperience entry's
 * riseActivity reference.
 *
 * @param {Object} entry - The normalized learningExperience entry
 * @returns {{ lessonId: string, expiration: (string|null) }|null}
 */
export const getRiseActivityLaunchInfo = entry => {
  const riseActivity = entry?.riseActivity
  if (!riseActivity) {
    return null
  }

  const lessonId = riseActivity.filename || riseActivity.fileName || null
  if (!lessonId) {
    return null
  }

  return {
    lessonId,
    expiration: riseActivity.expiration || null,
  }
}
